import { useState, useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { FiMic, FiMicOff, FiMonitor, FiPhoneOff } from 'react-icons/fi'
import { useCallActions } from '../../hooks/useCallActions'
import { useAuthContext } from '../../contexts/AuthContext'
import { useCallStore } from '../../stores/useCallStore'
import {
  AnimatedBackground,
  AvatarCircle,
  AvatarContainer,
  CenterContent,
  ControlBtn,
  ControlsContainer,
  FullscreenOverlay,
  ListeningRing,
  Orb,
  Particle,
  ScreenBadge,
  StatusSubtitle,
  StatusTitle,
  TranscriptLine,
  TranscriptWrapper,
  VisualizerBar,
  VisualizerContainer,
} from './CallOverlay.styles'

const CallOverlay = () => {
  const { user } = useAuthContext()

  // Custom hosok
  const { startScreenShare, stopScreenShare, toggleMic, endCall } = useCallActions()

  // Call Store
  const { isCallActive, isMicOn, isScreenOn, callStatus, callTranscript, audioLevel } =
    useCallStore()

  const [waveformBars, setWaveformBars] = useState(
    Array(24)
      .fill(0)
      .map(() => Math.random() * 0.5 + 0.2)
  )
  const [transcriptLines, setTranscriptLines] = useState([])
  const transcriptIdCounter = useRef(0)

  useEffect(() => {
    if (!isCallActive) return

    const interval = setInterval(() => {
      setWaveformBars(prev =>
        prev.map((_, i) => {
          const centerWeight = 1 - Math.abs(i - 12) / 12
          const baseHeight = Math.random() * 0.3 + 0.2
          const audioInfluence = (audioLevel / 255) * centerWeight
          return Math.min(baseHeight + audioInfluence, 1)
        })
      )
    }, 100)

    return () => clearInterval(interval)
  }, [isCallActive, audioLevel])

  useEffect(() => {
    if (!callTranscript) {
      setTranscriptLines([])
      return
    }

    const lines = callTranscript.split('\n').filter(line => line.trim())
    const newLines = lines.map((text, idx) => ({
      id: transcriptIdCounter.current++,
      text,
      timestamp: Date.now(),
    }))

    setTranscriptLines(newLines)

    // Auto-remove old lines after 8 seconds
    const timeout = setTimeout(() => {
      setTranscriptLines(prev =>
        prev.filter(line => Date.now() - line.timestamp < 8000)
      )
    }, 8100)

    return () => clearTimeout(timeout)
  }, [callTranscript])

  if (!isCallActive) return null

  const handleToggleScreen = () => {
    if (isScreenOn) {
      stopScreenShare()
    } else {
      startScreenShare()
    }
  }
  return (
    <FullscreenOverlay>
      <AnimatedBackground>
        <Orb
          color="#667eea 0%, #764ba2 100%"
          size="600px"
          top="-250px"
          left="-250px"
          delay="0s"
        />
        <Orb
          color="#f093fb 0%, #f5576c 100%"
          size="500px"
          bottom="-200px"
          right="-200px"
          delay="8s"
        />
        <Orb
          color="#4facfe 0%, #00f2fe 100%"
          size="450px"
          top="50%"
          left="50%"
          delay="16s"
          sx={{ transform: 'translate(-50%, -50%)' }}
        />

        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <Particle
            key={i}
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 15}s`,
              animationDuration: `${15 + Math.random() * 10}s`,
            }}
          />
        ))}
      </AnimatedBackground>

      <CenterContent>
        {/* Avatar with listening animation */}
        <AvatarContainer>
          <AvatarCircle listening={isMicOn}>
            {/* <Box className="avatar-icon">
              <img src="/franca-speaking-unscreen.gif" alt="F.R.A.N.C.A." />
            </Box> */}
            {isMicOn && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                }}
              >
                <ListeningRing delay="0s" />
                <ListeningRing delay="0.8s" />
                <ListeningRing delay="1.6s" />
              </Box>
            )}
          </AvatarCircle>
        </AvatarContainer>

        {/* Status text */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <StatusTitle variant="h2">Chiamata con F.R.A.N.C.A.</StatusTitle>
          <StatusSubtitle>{callStatus || 'Connesso'}</StatusSubtitle>
          {isScreenOn && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ScreenBadge>
                <FiMonitor size={14} />
                <span>Condivisione schermo attiva</span>
              </ScreenBadge>
            </Box>
          )}
        </Box>

        {/* Real-time waveform visualization */}
        <VisualizerContainer>
          {waveformBars.map((height, i) => (
            <VisualizerBar
              key={i}
              style={{
                height: `${height * 80}px`,
                animationDelay: `${i * 0.05}s`,
              }}
            />
          ))}
        </VisualizerContainer>
      </CenterContent>

      {/* Floating call controls - fixed at bottom */}
      <ControlsContainer>
        <ControlBtn
          onClick={toggleMic}
          isActive={isMicOn}
          variantType={isMicOn ? 'active' : 'muted'}
          title={isMicOn ? 'Silenzia microfono' : 'Attiva microfono'}
        >
          {isMicOn ? <FiMic /> : <FiMicOff />}
        </ControlBtn>

        {user?.feature_flags?.includes('screen_share') && (
          <ControlBtn
            onClick={handleToggleScreen}
            isActive={isScreenOn}
            variantType={isScreenOn ? 'active' : 'default'}
            title={isScreenOn ? 'Ferma condivisione' : 'Condividi schermo'}
          >
            <FiMonitor />
          </ControlBtn>
        )}

        <ControlBtn onClick={endCall} variantType="end-call" title="Termina chiamata">
          <FiPhoneOff />
        </ControlBtn>
      </ControlsContainer>

      {/* Floating transcript at bottom - auto-fade */}
      {transcriptLines.length > 0 && (
        <TranscriptWrapper>
          {transcriptLines.slice(-3).map((line, idx, recentLines) => {
            const age = Date.now() - line.timestamp
            const opacity = Math.max(0, 1 - age / 8000)
            return (
              <TranscriptLine
                key={line.id}
                style={{
                  opacity,
                  transform: `translateY(${(recentLines.length - idx - 1) * -10}px)`,
                }}
              >
                {line.text}
              </TranscriptLine>
            )
          })}
        </TranscriptWrapper>
      )}
    </FullscreenOverlay>
  )
}

export default CallOverlay
